import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink as RRNavLink } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import GoogleTranslate from "../components/GoogleTranslate";
import CommandPalette from "../components/CommandPalette";
import Breadcrumbs from "../components/Breadcrumbs";
import EnhancedTooltip from "../components/EnhancedTooltip";
import DarkModeToggle from "../components/DarkModeToggle";
import { ToastProvider } from "../components/ToastProvider";
import { authLogout } from "../actions/auth";
import { canAccess, hasAccessToAny } from "../utils/permissions";

// lucide-react icons
import {
  LayoutGrid,
  Menu as MenuIcon,
  LogOut,
  User as UserIcon,
  Monitor,
  Filter,
  Star,
  BarChart2,
  PieChart,
  Calculator,
  Target,
  Crown,
  FileSignature,
  Gamepad2,
  SlidersHorizontal,
  UserCog,
  ArrowRight,
  ArrowLeft,
  Clock,
  Users,
  Database,
  Download,
  Smartphone,
  Info,
  Search,
  TrendingUp,
  Boxes,
  Calendar,
  HelpCircle,
  BookOpen,
  MessageSquare,
  RefreshCw,
  Newspaper,
  ChevronDown
} from "lucide-react";

/**
 * Inject styles once on mount
 */
const injectStyles = () => {
  if (document.getElementById("app-shell-styles")) return;
  const css = `
/* ==================== VARIABLES ==================== */
:root {
  --vw-dark: #0f172a;
  --vw-dark-900: #0b1223;
  --vw-dark-gradient: linear-gradient(to bottom, #0f172a, #01315f);
  --vw-accent: #1e293b;
  --sidebar-w: 280px;
  --sidebar-collapsed-w: 72px;
  --sidebar-icons-w: 72px;
  --sidebar-sub-w: 260px;
  --topbar-h: 64px;
  --mobile-bottom-nav-h: 70px;
  --danger: #ef4444;
  --focus-ring: 0 0 0 3px rgba(59, 130, 246, 0.5);
  --focus-ring-light: 0 0 0 3px rgba(255, 255, 255, 0.3);
  
  /* Theme colors - Light mode (default) */
  --bg-primary: #f7f8fb;
  --bg-secondary: #ffffff;
  --text-primary: #111827;
  --text-secondary: #6b7280;
  --border-color: #e5e7eb;
  
  /* Topbar colors - Light mode */
  --topbar-bg: #ffffff;
  --topbar-text: #111827;
  --topbar-border: #e5e7eb;
  --topbar-btn-border: rgba(0, 0, 0, 0.1);
  --topbar-btn-hover: rgba(0, 0, 0, 0.05);
  --topbar-icon-color: #6b7280;
  
  /* Sidebar colors - Light mode */
  --sidebar-bg: #ffffff;
  --sidebar-icons-bg: #f8f9fa;
  --sidebar-border: #e5e7eb;
  --sidebar-text: #111827;
  --sidebar-text-secondary: #6b7280;
  --sidebar-icon-color: #6b7280;
  --sidebar-icon-active: #111827;
  --sidebar-btn-hover: rgba(0, 0, 0, 0.05);
  --sidebar-btn-active: rgba(0, 0, 0, 0.08);
  --sidebar-submenu-bg: #ffffff;
  --sidebar-submenu-label: #6b7280;
  --sidebar-submenu-border: #e5e7eb;
}

/* Dark mode variables */
.dark-mode {
  --bg-primary: #0f172a;
  --bg-secondary: #1e293b;
  --text-primary: #f9fafb;
  --text-secondary: #d1d5db;
  --border-color: #374151;
  --dark-gradient: linear-gradient(135deg,#022040,#0a4080);
  
  /* Topbar colors - Dark mode */
  --topbar-bg: var(--dark-gradient);
  --topbar-text: #ffffff;
  --topbar-border: rgba(255, 255, 255, 0.08);
  --topbar-btn-border: rgba(255, 255, 255, 0.2);
  --topbar-btn-hover: rgba(255, 255, 255, 0.1);
  --topbar-icon-color: #ffffff;
  
  /* Sidebar colors - Dark mode */
  --sidebar-bg: var(--dark-gradient);
  --sidebar-icons-bg: rgba(15, 23, 42, 0.95);
  --sidebar-border: rgba(255, 255, 255, 0.09);
  --sidebar-text: #ffffff;
  --sidebar-text-secondary: #cbd5e1;
  --sidebar-icon-color: #9ca3af;
  --sidebar-icon-active: #ffffff;
  --sidebar-btn-hover: rgba(255, 255, 255, 0.1);
  --sidebar-btn-active: rgba(255, 255, 255, 0.08);
  --sidebar-submenu-bg: linear-gradient(to bottom, #0f172a, #01315f);
  --sidebar-submenu-label: #9ca3af;
  --sidebar-submenu-border: rgba(255, 255, 255, 0.05);
}

.dark-mode .shell {
  background-color: #f7f8fb;
}

.dark-mode .breadcrumbs {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}

.dark-mode .breadcrumbs-link,
.dark-mode .breadcrumbs-text {
  color: var(--text-secondary);
}

.dark-mode .breadcrumbs-current {
  color: var(--text-primary);
}

.dark-mode .command-palette {
  background: var(--dark-gradient);
  border: 1px solid var(--border-color);
}

.dark-mode .command-palette-input {
  color: var(--text-primary);
}

.dark-mode .command-palette-input::placeholder {
  color: var(--text-secondary);
}

.dark-mode .command-palette-item-label {
  color: var(--text-primary);
}

.dark-mode .command-palette-item-description {
  color: var(--text-secondary);
}

.dark-mode .command-palette-item:hover,
.dark-mode .command-palette-item.selected {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .toast-item {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}

.dark-mode .toast-title {
  color: var(--text-primary);
}

/* ==================== TOPBAR ==================== */
.topbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: var(--topbar-h);
  background: var(--topbar-bg);
  color: #fff;
  display: flex;
  align-items: center;
  z-index: 1030;
  box-shadow: 0 2px 8px rgba(0,0,0,.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.topbar__inner {
  width: 100%;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.topbar__left, .topbar__right {
  display: flex;
  align-items: center;
}

.topbar__brand img {
  height: 30px;
  display: block;
}

/* Hide dark logo in light mode, show light logo */
.topbar__brand .logo-dark {
  display: none;
}

.topbar__brand .logo-light {
  display: block;
}

/* Hide light logo in dark mode, show dark logo */
.dark-mode .topbar__brand .logo-dark {
  display: block;
}

.dark-mode .topbar__brand .logo-light {
  display: none;
}

.topbar__brand:focus {
  outline: none;
  box-shadow: var(--focus-ring);
  border-radius: 8px;
}

/* ── platform switcher ── */
.psw-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  border-radius: 10px;
  color: var(--topbar-text);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.psw-chip:hover { background: var(--topbar-btn-hover); transform: translateY(-1px); }
.psw-chip:active { transform: translateY(0); }
.psw-chip:focus  { outline: none; }
.psw-chip-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.psw-chip-chevron { transition: transform .2s; opacity: .7; }
.psw-chip-chevron--open { transform: rotate(180deg); }
.psw-chip-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1999;
  background: var(--topbar-bg);
  border: 1px solid var(--topbar-border);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,.18);
  padding: 8px;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: psw-fadein .15s ease;
}
@keyframes psw-fadein {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.psw-chip-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 9px;
  text-decoration: none !important;
  color: var(--topbar-text) !important;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .05em;
  transition: background .12s;
}
.psw-chip-card:hover { background: var(--topbar-btn-hover); }
.psw-chip-card-accent { width: 4px; height: 28px; border-radius: 4px; flex-shrink: 0; }
.psw-chip-card-label { flex: 1; }

.btn-ghost {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  gap: 6px;
}
.btn-ghost:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.btn-ghost:active {
  transform: translateY(0);
}
.btn-ghost:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nightscan-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.nightscan-btn:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.nightscan-btn:active {
  transform: translateY(0);
}
.nightscan-btn:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nightscan-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--danger);
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

.dropdown-toggle:after {
  color: var(--topbar-text) !important;
}

/* Language dropdown styling */
.nav-item.dropdown {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
}

.dropdown-toggle {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
}

.dropdown-toggle::after {
  margin-left: 4px !important;
}

/* Mobile-specific topbar */
.topbar__mobileToggle { display: none; }
@media (max-width: 768px) {
  .topbar__right .btn-ghost.user-btn,
  .topbar__right .btn-ghost.logout-btn { display: none; }
  .topbar__mobileToggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 1px solid var(--topbar-btn-border);
    color: var(--topbar-text);
    padding: 6px 10px;
    border-radius: 10px;
    margin-left: 8px;
  }
  .topbar__mobileToggle:hover { background: var(--topbar-btn-hover); }
}

/* Hide list markers in dropdowns */
.dropdown-menu li::marker,
.nav-item::marker {
  display: none;
  content: none;
}

/* Ensure dropdowns appear above other elements */
.dropdown-menu {
  z-index: 1050;
}

.nav-item {
  list-style: none;
}

/* ==================== LAYOUT ==================== */
.shell {
  display: flex;
  min-height: 100vh;
  background: #f7f8fb;
}

/* ==================== SIDEBAR PRO ==================== */
.sidebar-pro {
  position: fixed;
  top: var(--topbar-h);
  left: 0;
  bottom: 0;
  display: flex;
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  z-index: 1020;
  box-shadow: 2px 0 8px rgba(0,0,0,.15);
  transition: all .25s ease;
}

/* Left icons column */
.sidebar-icons {
  width: var(--sidebar-icons-w);
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
  padding-top: 10px;
}

.icon-btn {
  background: transparent;
  color: var(--sidebar-icon-color);
  border: none;
  padding: 14px 0;
  cursor: pointer;
  width: 100%;
  min-height: 72px;
  display: flex;
  justify-content: center;
  transition: all .2s ease;
  outline: none;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.icon-btn:hover, .icon-btn.active {
  color: var(--sidebar-icon-active);
  background: var(--sidebar-btn-hover);
}

.icon-btn:focus {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.5);
}

.icon-btn:focus:not(:focus-visible) {
  box-shadow: none;
}

.icon-label {
  font-size: 0.7rem;
  margin-top: 0.25rem;
}

/* Right submenu panel */
.submenu-panel {
  width: 0;
  overflow: hidden;
  background: var(--sidebar-bg);
  transition: width .25s ease;
}
.submenu-panel.open { width: var(--sidebar-sub-w); }

.submenu-content {
  opacity: 0;
  transform: translateX(-10px);
  transition: opacity .25s ease, transform .25s ease;
  padding: 16px 12px;
  height: 100%;
  overflow-y: auto;
}
.submenu-panel.open .submenu-content {
  opacity: 1;
  transform: translateX(0);
}

/* Submenu links */
.submenu-label {
  font-size: 11px;
  text-transform: uppercase;
  color: var(--sidebar-submenu-label);
  padding: 8px 12px 4px;
  margin-top: 8px;
  letter-spacing: .05em;
  border-bottom: 1px solid var(--sidebar-submenu-border);
}

.submenu-content .nav-button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  color: var(--sidebar-text-secondary);
  background: transparent;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  transition: background .12s ease, color .12s ease;
  border: none;
  cursor: pointer;
  text-align: left;
  margin-top: 5px;
}
.submenu-content .nav-button:hover,
.submenu-content .nav-button.active {
  background: var(--sidebar-btn-active);
  color: var(--sidebar-text);
  transform: translateX(4px);
}

.submenu-content .nav-button:active {
  transform: translateX(2px);
}
.submenu-content .nav-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
.submenu-content .nav-button svg {
  min-width: 20px;
  opacity: 0.9;
}

/* Scrollbars for submenu */
.submenu-content::-webkit-scrollbar { width: 8px; }
.submenu-content::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.25);
  border-radius: 999px;
}
.submenu-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,0.5);
}

/* Content shifting (desktop only) */
.content.shifted {
  margin-left: calc(var(--sidebar-icons-w) + var(--sidebar-sub-w));
  max-width: calc(100vw - (var(--sidebar-icons-w) + var(--sidebar-sub-w)));
}
.content.not-shifted {
  margin-left: var(--sidebar-icons-w);
  max-width: calc(100vw - var(--sidebar-icons-w));
}
.content {
  flex: 1;
  padding-top: var(--topbar-h);
  transition: margin-left .25s ease;
  max-width: calc(100vw - var(--sidebar-icons-w));
}

/* ==================== NIGHTSCAN RIGHT DRAWER ==================== */
.rightpanel {
  position: fixed;
  top: var(--topbar-h);
  right: 0;
  bottom: 0;
  width: 360px;
  max-width: 92vw;
  background: #fff;
  color: #0f172a;
  border-left: 1px solid rgba(0,0,0,.1);
  box-shadow: -2px 0 12px rgba(0,0,0,.12);
  transform: translateX(100%);
  transition: transform .22s ease;
  z-index: 1040;
  display: flex;
  flex-direction: column;
}
.rightpanel--open { transform: translateX(0); }
.rightpanel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: #f8fafc;
}
.rightpanel__title { font-weight: 600; }
.rightpanel__body { padding: 12px; overflow-y: auto; }
.rightpanel__item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(0,0,0,.08);
  background: #fff;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all .15s ease;
}
.rightpanel__item:hover {
  border-color: #0015ce;
  background: #0312e71a;
  transform: translateX(-4px);
  box-shadow: 0 2px 8px rgba(0, 21, 206, 0.15);
}

.rightpanel__item:active {
  transform: translateX(-2px);
}
.status-badge {
  display: inline-block;
  color: #fff;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}
.rightpanel__empty {
  padding: 24px;
  color: #64748b;
  text-align: center;
}
.backdrop-right {
  position: fixed;
  top: var(--topbar-h);
  left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.45);
  z-index: 1035;
}

/* ==================== MOBILE BOTTOM TAB BAR ==================== */
@media (max-width: 768px) {
  .mobile-bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: var(--mobile-bottom-nav-h);
    background: var(--sidebar-bg);
    border-top: 1px solid var(--sidebar-border);
    display: flex;
    align-items: center;
    justify-content: space-around;
    z-index: 1030;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    padding: 0 8px;
    opacity: 1;
  }

  .dark-mode .mobile-bottom-nav {
    background: var(--dark-gradient);
    backdrop-filter: blur(10px);
  }

  .mobile-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: var(--sidebar-icon-color);
    padding: 8px 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;
    position: relative;
    min-width: 0;
  }

  .mobile-tab:active {
    transform: scale(0.95);
  }

  .mobile-tab.active {
    color: var(--sidebar-icon-active);
    background: var(--sidebar-btn-active);
  }

  .mobile-tab-label {
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .mobile-tab-badge {
    position: absolute;
    top: 6px;
    right: 8px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--danger);
    color: #fff;
    font-size: 10px;
    border-radius: 999px;
    line-height: 1;
  }

  /* Mobile menu modal (slides from bottom) */
  .mobile-menu-modal {
    position: fixed;
    bottom: var(--mobile-bottom-nav-h);
    left: 0;
    right: 0;
    max-height: calc(85vh - var(--mobile-bottom-nav-h));
    background: var(--sidebar-bg);
    border-radius: 20px 20px 0 0;
    z-index: 1029;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
  }

  .mobile-menu-modal.open {
    transform: translateY(0);
  }

  .mobile-menu-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--sidebar-border);
    background: var(--sidebar-bg);
    border-radius: 20px 20px 0 0;
  }

  .mobile-menu-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--sidebar-text);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mobile-menu-close {
    background: transparent;
    border: none;
    color: var(--sidebar-icon-color);
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
  }

  .mobile-menu-close:hover {
    background: var(--sidebar-btn-hover);
  }

  .mobile-menu-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: visible;
    padding: 16px;
  }

  /* Language dropdown in mobile needs proper positioning */
  @media (max-width: 768px) {
    .mobile-menu-content .dropdown-menu {
      position: absolute !important;
      top: auto !important;
      bottom: 100% !important;
      left: 50% !important;
      right: auto !important;
      transform: translateX(-50%) translateY(-8px) !important;
      margin: 0 !important;
      z-index: 9999 !important;
      max-height: 300px;
      overflow-y: auto;
      padding: 10rem .5rem;
    }

    .mobile-menu-content .dropdown {
      position: relative !important;
    }
  }

  .mobile-menu-section {
    margin-bottom: 20px;
  }

  .mobile-menu-section-title {
    font-size: 11px;
    text-transform: uppercase;
    color: var(--sidebar-submenu-label);
    padding: 8px 12px 4px;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--sidebar-submenu-border);
    margin-bottom: 8px;
  }

  .mobile-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    color: var(--sidebar-text-secondary);
    background: transparent;
    padding: 14px 12px;
    border-radius: 10px;
    text-decoration: none;
    transition: all 0.2s ease;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 15px;
  }

  .mobile-menu-item:active {
    transform: scale(0.98);
  }

  .mobile-menu-item:hover,
  .mobile-menu-item.active {
    background: var(--sidebar-btn-active);
    color: var(--sidebar-text);
  }

  .mobile-menu-item svg {
    min-width: 22px;
    opacity: 0.9;
  }

  /* Profile menu specific styles */
  .mobile-profile-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    background: var(--sidebar-icons-bg);
    border-bottom: 1px solid var(--sidebar-border);
  }

  .mobile-profile-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--sidebar-btn-active);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sidebar-icon-active);
  }

  .mobile-profile-info {
    flex: 1;
  }

  .mobile-profile-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--sidebar-text);
    margin-bottom: 2px;
  }

  .mobile-profile-role {
    font-size: 13px;
    color: var(--sidebar-text-secondary);
  }

  .mobile-menu-divider {
    height: 1px;
    background: var(--sidebar-border);
    margin: 12px 0;
  }

  .content { 
    margin-left: 0 !important;
    max-width: 100vw !important;
    padding-bottom: calc(var(--mobile-bottom-nav-h) + 20px) !important;
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1028;
    backdrop-filter: blur(4px);
  }

  /* Hide desktop sidebar on mobile */
  .sidebar-pro {
    display: none;
  }
}

/* ==================== REDUCED MOTION ==================== */
@media (prefers-reduced-motion: reduce) {
  .nav-button,
  .submenu-panel,
  .submenu-content,
  .content,
  .sidebar-pro,
  .rightpanel {
    transition: none !important;
  }
}

/* ==================== COMMAND PALETTE ==================== */
.command-palette-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2000;
  animation: fadeIn 0.15s ease;
}

.command-palette {
  position: fixed;
  top: 15vh;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 640px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: 2001;
  overflow: hidden;
  animation: slideDown 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from { 
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to { 
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.command-palette-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.command-palette-search-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #111827;
  background: transparent;
}

.command-palette-input::placeholder {
  color: #9ca3af;
}

.command-palette-kbd {
  padding: 4px 8px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  font-weight: 600;
}

.command-palette-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.command-palette-content::-webkit-scrollbar { width: 8px; }
.command-palette-content::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 999px;
}
.command-palette-content::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.command-palette-empty {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.command-palette-group {
  margin-bottom: 12px;
}

.command-palette-group-label {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
}

.command-palette-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;
}

.command-palette-item:hover,
.command-palette-item.selected {
  background: #f3f4f6;
  transform: translateX(2px);
}

.command-palette-item:active {
  transform: scale(0.99);
}

.command-palette-item-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.command-palette-item-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-item-content {
  flex: 1;
  min-width: 0;
}

.command-palette-item-label {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 2px;
}

.command-palette-item-description {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.command-palette-item-arrow {
  color: #9ca3af;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.command-palette-item:hover .command-palette-item-arrow,
.command-palette-item.selected .command-palette-item-arrow {
  opacity: 1;
}

.command-palette-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  color: #6b7280;
  background: transparent;
}

.command-palette-footer-item {
  display: flex;
  align-items: center;
  gap: 6px;
  display: none;
}

.command-palette-footer kbd {
  padding: 2px 6px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

/* ==================== BREADCRUMBS ==================== */
.breadcrumbs {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 24px;
  position: sticky;
  top: var(--topbar-h);
  z-index: 100;
}

.breadcrumbs-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.breadcrumbs-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.breadcrumbs-link,
.breadcrumbs-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.15s ease, transform 0.15s ease;
}

.breadcrumbs-link:hover {
  color: #3b82f6;
  transform: translateY(-1px);
}

.dark-mode .breadcrumbs-link:hover {
  color: #60a5fa;
}

.breadcrumbs-link:active {
  transform: translateY(0);
}

.breadcrumbs-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 4px;
}

.breadcrumbs-current {
  color: #111827;
  font-weight: 500;
}

.breadcrumbs-icon {
  flex-shrink: 0;
}

.breadcrumbs-separator {
  color: #d1d5db;
  flex-shrink: 0;
}

/* ==================== ENHANCED TOOLTIP ==================== */
.enhanced-tooltip {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  animation: tooltipFadeIn 0.15s ease;
}

@keyframes tooltipFadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.enhanced-tooltip-content {
  background: #1f2937;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 280px;
}

.enhanced-tooltip--rich .enhanced-tooltip-content {
  padding: 10px 14px;
}

.enhanced-tooltip-simple {
  white-space: nowrap;
}

.enhanced-tooltip-rich {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.enhanced-tooltip-title {
  font-weight: 600;
  font-size: 13px;
}

.enhanced-tooltip-description {
  font-size: 12px;
  color: #d1d5db;
  line-height: 1.5;
}

.enhanced-tooltip-shortcut {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.enhanced-tooltip-kbd {
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

.enhanced-tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}

.enhanced-tooltip-arrow--top {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0 6px;
  border-color: #1f2937 transparent transparent transparent;
}

.enhanced-tooltip-arrow--bottom {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 0 6px 6px 6px;
  border-color: transparent transparent #1f2937 transparent;
}

.enhanced-tooltip-arrow--left {
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 0 6px 6px;
  border-color: transparent transparent transparent #1f2937;
}

.enhanced-tooltip-arrow--right {
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 6px 6px 0;
  border-color: transparent #1f2937 transparent transparent;
}

/* ==================== TOAST NOTIFICATIONS ==================== */
.toast-container {
  position: fixed;
  top: calc(var(--topbar-h) + 20px);
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.toast-item {
  pointer-events: all;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 320px;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px var(--toast-border);
  animation: toastSlideIn 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
  transition: all 0.2s ease;
}

.toast-item:hover {
  transform: translateX(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2), 0 0 0 1px var(--toast-border);
}

@keyframes toastSlideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  margin-bottom: 2px;
}

.toast-message {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.toast-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  background: #f3f4f6;
  color: #111827;
  transform: scale(1.1);
}

.toast-close:active {
  transform: scale(0.95);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .toast-container {
    right: 12px;
    left: 12px;
    top: calc(var(--topbar-h) + 12px);
  }
  
  .toast-item {
    min-width: 0;
    max-width: 100%;
  }
}

/* ==================== DARK MODE TOGGLE ==================== */
.dark-mode-toggle {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  border-radius: 20px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.dark-mode-toggle:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}

.dark-mode-toggle:active {
  transform: translateY(0);
}

.dark-mode-toggle:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.dark-mode-toggle-track {
  width: 44px;
  height: 22px;
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  transition: background 0.3s ease;
}

.dark-mode-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-mode-icon {
  color: #f59e0b;
}

/* Dark mode styles */
.dark-mode .dark-mode-toggle-track {
  background: rgba(59, 130, 246, 0.3);
}

.dark-mode .dark-mode-toggle-thumb {
  left: 24px;
  background: #3b82f6;
}

.dark-mode .dark-mode-icon {
  color: #fff;
}
  `;
  const el = document.createElement("style");
  el.id = "app-shell-styles";
  el.innerHTML = css;
  document.head.appendChild(el);
};

/**
 * TopBar Component
 */
const TopBar = ({
  fullname,
  userId,
  onLogout,
  isMobile,
  role,
  type,
  category,
  onToggleSidebarMobile,
  onOpenCommandPalette,
  onGoToUser
}) => {
  const [showChipMenu, setShowChipMenu] = useState(false);
  const chipCloseTimer = useRef(null);

  const PLATFORMS = [
    { label: 'LOCKERS',    href: 'https://lockers.vacway.es',   color: '#3b82f6' },
    { label: 'TICKETING',  href: 'https://ticketing.vacway.es', color: '#f97316' },
    { label: 'FASTPASS',   href: 'https://fastpass.vacway.es',  color: '#8b5cf6' },
    { label: 'CLOCK',      href: 'https://clock.vacway.es',     color: '#06b6d4' },
  ];

  return (
    <div className="topbar">
      <div className="topbar__inner">
        <div className="topbar__left">
          <Link to="/" className="topbar__brand mr-3" aria-label="Home">
            <img src="/images/logo_white.png" alt="Logo" className="logo-dark" />
            <img src="/images/logo_blue.png" alt="Logo" className="logo-light" />
          </Link>
          {/* ── Platform switcher ── */}
          <div
            style={{ position: 'relative' }}
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') { clearTimeout(chipCloseTimer.current); setShowChipMenu(true); } }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') { chipCloseTimer.current = setTimeout(() => setShowChipMenu(false), 120); } }}
          >
            <button
              className="psw-chip"
              onClick={() => setShowChipMenu(v => !v)}
              aria-label="Cambiar plataforma"
            >
              <span className="psw-chip-dot" style={{ background: '#84cc16' }} />
              <span>WATERPROOF</span>
              <ChevronDown size={13} className={`psw-chip-chevron${showChipMenu ? ' psw-chip-chevron--open' : ''}`} />
            </button>
            {showChipMenu && (
              <div className="psw-chip-panel">
                {PLATFORMS.map(p => (
                  <a key={p.label} href={p.href} className="psw-chip-card">
                    <span className="psw-chip-card-accent" style={{ background: p.color }} />
                    <span className="psw-chip-card-label">{p.label}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="topbar__right">
          {/* Command Palette trigger */}
          <EnhancedTooltip
            content="Búsqueda rápida"
            description="Buscar y navegar rápidamente"
            shortcut={['⌘K']}
            position="bottom"
          >
            <button
              className="btn-ghost"
              onClick={onOpenCommandPalette}
              title="Búsqueda rápida"
              aria-label="Abrir búsqueda rápida (Cmd+K)"
              style={{ marginRight: 8 }}
            >
              <Search size={18} />
              {!isMobile && <span style={{ marginLeft: 6 }}>Buscar</span>}
            </button>
          </EnhancedTooltip>

          <div className="mr-2 d-none d-md-flex"><GoogleTranslate /></div>

          {/* Dark Mode Toggle */}
          {!isMobile && (
            <div style={{ marginRight: 8 }}>
              <EnhancedTooltip 
                content="Tema" 
                description="Cambiar entre modo claro y oscuro"
                position="bottom"
              >
                <DarkModeToggle />
              </EnhancedTooltip>
            </div>
          )}

          {/* Desktop-only actions */}
          {!isMobile && (
            <>
              <EnhancedTooltip content={fullname} position="bottom">
                <button type="button" className="btn-ghost user-btn" title={fullname} onClick={() => onGoToUser(userId)}>
                  {role === 'ADMIN' ? (
                    <img
                      src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                      alt={fullname}
                      style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', marginRight: 6 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                      }}
                    />
                  ) : (
                    <UserIcon size={18} />
                  )}
                  <span className="d-none d-sm-inline">{fullname}</span>
                </button>
              </EnhancedTooltip>
              <EnhancedTooltip content="Cerrar sesión" position="bottom">
                <button className="btn-ghost logout-btn" onClick={onLogout} style={{ marginLeft: 8 }}>
                  <LogOut size={18} />
                  <span className="d-none d-sm-inline">Salir</span>
                </button>
              </EnhancedTooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar Component (Desktop)
 */
const Sidebar = ({ role, type, category, onNavigate, activeMenu, setActiveMenu }) => {
  const toggleMenu = (menu) => setActiveMenu(prev => (prev === menu ? null : menu));

  // Check access to menu categories
  const hasPuntosVenta = hasAccessToAny(role, type, category, ['machines', 'minis']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'incidences', 'audit', 'modules', 'matrixs', 'tpvs']);
  const hasVentas = hasAccessToAny(role, type, category, ['sales', 'arqueos', 'arqueosSales', 'objectives', 'comissions']);
  const hasFichajes = hasAccessToAny(role, type, category, ['shifts', 'shiftsAdmin']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'supporttickets', 'supportchat', 'faqs', 'manuals', 'api-docs']);
  const hasAdmin = hasAccessToAny(role, type, category, ['promoters', 'users', 'logistics']);

  const NavLinkItem = ({ to, icon: Icon, children }) => (
    <RRNavLink
      to={to}
      className="nav-button"
      onClick={() => {
        setActiveMenu(null);
        onNavigate();
      }}
    >
      {Icon && <Icon size={16} />}
      <span>{children}</span>
    </RRNavLink>
  );

  return (
    <div className="sidebar-pro">
      <div className="sidebar-icons">
        {hasPuntosVenta && (
          <EnhancedTooltip content="Canales" description="Gestión de ONEs y MINIs" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "puntos" ? "active" : ""}`}
              onClick={() => toggleMenu("puntos")}
              title="Canales"
              aria-label="Menú de canales"
            >
              <Monitor size={20} />
              <span className="icon-label">Canales</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasOperativa && (
          <EnhancedTooltip content="Operativa" description="Gestión operativa del sistema" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "operativa" ? "active" : ""}`}
              onClick={() => toggleMenu("operativa")}
              title="Operativa"
              aria-label="Menú de operativa"
            >
              <SlidersHorizontal size={20} />
              <span className="icon-label">Operativa</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasVentas && (
          <EnhancedTooltip content="Reporte" description="Análisis y reportes de ventas" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "ventas" ? "active" : ""}`}
              onClick={() => toggleMenu("ventas")}
              title="Reporte"
              aria-label="Menú de reporte"
            >
              <TrendingUp size={20} />
              <span className="icon-label">Reporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasFichajes && (
          <EnhancedTooltip content="Fichajes" description="Control de jornadas laborales" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "fichajes" ? "active" : ""}`}
              onClick={() => toggleMenu("fichajes")}
              title="Fichajes"
              aria-label="Menú de fichajes"
            >
              <Clock size={20} />
              <span className="icon-label">Fichajes</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasSoporte && (
          <EnhancedTooltip content="Soporte" description="Recursos de ayuda y soporte" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "soporte" ? "active" : ""}`}
              onClick={() => toggleMenu("soporte")}
              title="Soporte"
              aria-label="Menú de soporte"
            >
              <HelpCircle size={20} />
              <span className="icon-label">Soporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasAdmin && (
          <EnhancedTooltip content="Administración" description="Configuración y administración" position="right" delay={300}>
            <button
              className={`icon-btn ${activeMenu === "admin" ? "active" : ""}`}
              onClick={() => toggleMenu("admin")}
              title="Administración"
              aria-label="Menú de administración"
            >
              <Crown size={20} />
              <span className="icon-label">Admin</span>
            </button>
          </EnhancedTooltip>
        )}
      </div>

      <div className={`submenu-panel ${activeMenu ? "open" : ""}`}>
        {activeMenu === "puntos" && (
          <div className="submenu-content">
            <div className="submenu-label">Canales</div>
            {canAccess(role, type, category, 'machines') && (
              <NavLinkItem to="/machines" icon={Monitor}>ONEs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'minis') && (
              <NavLinkItem to="/minis" icon={Boxes}>MINIs</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "operativa" && (
          <div className="submenu-content">
            <div className="submenu-label">Operativa</div>
            {canAccess(role, type, category, 'customers') && (
              <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
            )}
            {canAccess(role, type, category, 'contracts') && (
              <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'incidences') && (
              <NavLinkItem to="/incidences" icon={Info}>Incidencias</NavLinkItem>
            )}
            {canAccess(role, type, category, 'audit') && (
              <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tpvs') && (
              <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "ventas" && (
          <div className="submenu-content">
            <div className="submenu-label">Reporte</div>
            {canAccess(role, type, category, 'sales') && (
              <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'arqueos') && (
              <>
                <NavLinkItem to="/arqueos" icon={BarChart2}>Gráficos</NavLinkItem>
                <NavLinkItem to="/arqueossales" icon={PieChart}>KPI</NavLinkItem>
              </>
            )}
            {role === 'ADMIN' && (
              <NavLinkItem to="/dashboard" icon={TrendingUp}>Dashboard</NavLinkItem>
            )}
            {canAccess(role, type, category, 'objectives') && (
              <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'comissions') && (
              <NavLinkItem to="/comissions" icon={Calculator}>Comisiones</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "fichajes" && (
          <div className="submenu-content">
            <div className="submenu-label">Fichajes</div>
            {canAccess(role, type, category, 'shifts') && (
              <NavLinkItem to="/shifts" icon={Clock}>Jornada Promotor</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "soporte" && (
          <div className="submenu-content">
            <div className="submenu-label">Soporte</div>
            {canAccess(role, type, category, 'supportticketadd') && (
              <NavLinkItem to="/supportticketadd" icon={Info}>Crear Ticket</NavLinkItem>
            )}
            {canAccess(role, type, category, 'supporttickets') && (
              <NavLinkItem to="/supporttickets" icon={Info}>Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'supportchat') && (
              <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faqs') && (
              <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'manuals') && (
              <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
            )}
            {canAccess(role, type, category, 'api-docs') && (
              <NavLinkItem to="/api-docs" icon={Newspaper}>API</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "admin" && (
          <div className="submenu-content">
            <div className="submenu-label">Administración</div>
            {canAccess(role, type, category, 'promoters') && (
              <NavLinkItem to="/promoters" icon={Users}>Promotores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'users') && (
              <NavLinkItem to="/users" icon={UserCog}>Trabajadores</NavLinkItem>
            )}
            {type === 'MASTER' && (
              <NavLinkItem to="/sync" icon={RefreshCw}>Base de Datos Sync</NavLinkItem>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Mobile Menu Modal
 */
const MobileMenuModal = ({
  open,
  onClose,
  title,
  icon: Icon,
  children,
  isProfile = false,
  fullname,
  role
}) => {
  return (
    <>
      {open && <div className="backdrop" onClick={onClose} />}
      <div className={`mobile-menu-modal ${open ? "open" : ""}`}>
        {isProfile ? (
          <div className="mobile-profile-header">
            <div className="mobile-profile-avatar">
              {role === 'ADMIN' ? (
                <img
                  src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                  alt={fullname}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                  }}
                />
              ) : (
                <UserIcon size={24} />
              )}
            </div>
            <div className="mobile-profile-info">
              <div className="mobile-profile-name">{fullname}</div>
              <div className="mobile-profile-role">{role}</div>
            </div>
          </div>
        ) : (
          <div className="mobile-menu-header">
            <div className="mobile-menu-title">
              {Icon && <Icon size={22} />}
              {title}
            </div>
            <button className="mobile-menu-close" onClick={onClose} aria-label="Cerrar menú">
              <ArrowLeft size={22} />
            </button>
          </div>
        )}
        <div className="mobile-menu-content">
          {children}
        </div>
      </div>
    </>
  );
};

/**
 * Mobile Bottom Navigation
 */
const MobileBottomNav = ({
  role,
  type,
  category,
  onNavigate,
  activeTab,
  setActiveTab,
  fullname,
  userId,
  onLogout,
  onGoToUser
}) => {
  // Configure which sections appear in bottom bar vs "More" menu
  const favoriteMenus = ['puntos', 'ventas', 'operativa']; // Edit this to change favorites

  // Check permissions
  const hasPuntosVenta = hasAccessToAny(role, type, category, ['machines', 'minis']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'incidences', 'audit', 'modules', 'matrixs', 'tpvs']);
  const hasVentas = hasAccessToAny(role, type, category, ['sales', 'arqueos', 'arqueosSales', 'objectives', 'comissions']);
  const hasFichajes = hasAccessToAny(role, type, category, ['shifts', 'shiftsAdmin']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'supporttickets', 'supportchat', 'faqs', 'manuals', 'api-docs']);
  const hasAdmin = hasAccessToAny(role, type, category, ['promoters', 'users', 'logistics']);

  const menuSections = {
    puntos: { has: hasPuntosVenta, icon: Monitor, label: 'Canales', isFavorite: favoriteMenus.includes('puntos') },
    operativa: { has: hasOperativa, icon: SlidersHorizontal, label: 'Operativa', isFavorite: favoriteMenus.includes('operativa') },
    ventas: { has: hasVentas, icon: TrendingUp, label: 'Reporte', isFavorite: favoriteMenus.includes('ventas') },
    fichajes: { has: hasFichajes, icon: Clock, label: 'Fichajes', isFavorite: favoriteMenus.includes('fichajes') },
    soporte: { has: hasSoporte, icon: HelpCircle, label: 'Soporte', isFavorite: favoriteMenus.includes('soporte') },
    admin: { has: hasAdmin, icon: Crown, label: 'Admin', isFavorite: favoriteMenus.includes('admin') }
  };

  const favoriteTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && config.isFavorite)
    .map(([key]) => key);

  const moreTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && !config.isFavorite)
    .map(([key]) => key);

  const hasMore = moreTabs.length > 0;

  const NavLinkItem = ({ to, icon: Icon, children }) => (
    <RRNavLink
      to={to}
      className="mobile-menu-item"
      onClick={() => {
        setActiveTab(null);
        onNavigate();
      }}
    >
      {Icon && <Icon size={20} />}
      <span>{children}</span>
    </RRNavLink>
  );

  return (
    <>
      <div className="mobile-bottom-nav">
        {favoriteTabs.map((tab) => {
          const config = menuSections[tab];
          const Icon = config.icon;
          return (
            <button
              key={tab}
              className={`mobile-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(prev => prev === tab ? null : tab)}
            >
              <Icon size={22} />
              <span className="mobile-tab-label">{config.label}</span>
            </button>
          );
        })}

        {hasMore && (
          <button
            className={`mobile-tab ${activeTab === "more" ? "active" : ""}`}
            onClick={() => setActiveTab(prev => prev === "more" ? null : "more")}
          >
            <LayoutGrid size={22} />
            <span className="mobile-tab-label">Más</span>
          </button>
        )}

        <button
          className={`mobile-tab ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab(prev => prev === "profile" ? null : "profile")}
        >
          <UserIcon size={22} />
          <span className="mobile-tab-label">Perfil</span>
        </button>
      </div>

      {/* Mobile Menus */}
      {favoriteTabs.map((tab) => {
        const config = menuSections[tab];
        const Icon = config.icon;
        return (
          <MobileMenuModal key={tab} open={activeTab === tab} onClose={() => setActiveTab(null)} title={config.label} icon={Icon}>
            <div className="mobile-menu-section">
              {tab === 'puntos' && (
                <>
                  <div className="mobile-menu-section-title">Canales</div>
                  {canAccess(role, type, category, 'machines') && (
                    <NavLinkItem to="/machines" icon={Monitor}>ONEs</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'minis') && (
                    <NavLinkItem to="/minis" icon={Boxes}>MINIs</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'operativa' && (
                <>
                  <div className="mobile-menu-section-title">Operativa</div>
                  {canAccess(role, type, category, 'customers') && (
                    <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'contracts') && (
                    <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'incidences') && (
                    <NavLinkItem to="/incidences" icon={Info}>Incidencias</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'audit') && (
                    <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'tpvs') && (
                    <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'ventas' && (
                <>
                  <div className="mobile-menu-section-title">Reporte</div>
                  {canAccess(role, type, category, 'sales') && (
                    <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'arqueos') && (
                    <>
                      <NavLinkItem to="/arqueos" icon={BarChart2}>Gráficos</NavLinkItem>
                      <NavLinkItem to="/arqueossales" icon={PieChart}>KPI</NavLinkItem>
                    </>
                  )}
                  {role === 'ADMIN' && (
                    <NavLinkItem to="/dashboard" icon={TrendingUp}>Dashboard</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'objectives') && (
                    <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'comissions') && (
                    <NavLinkItem to="/comissions" icon={Calculator}>Comisiones</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'fichajes' && (
                <>
                  <div className="mobile-menu-section-title">Fichajes</div>
                  {canAccess(role, type, category, 'shifts') && (
                    <NavLinkItem to="/shifts" icon={Clock}>Jornada Promotor</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'soporte' && (
                <>
                  <div className="mobile-menu-section-title">Soporte</div>
                  {canAccess(role, type, category, 'supportticketadd') && (
                    <NavLinkItem to="/supportticketadd" icon={Info}>Crear Ticket</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'supporttickets') && (
                    <NavLinkItem to="/supporttickets" icon={Info}>Tickets</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'supportchat') && (
                    <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'faqs') && (
                    <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'manuals') && (
                    <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
                  )}
                </>
              )}

              {tab === 'admin' && (
                <>
                  <div className="mobile-menu-section-title">Administración</div>
                  {canAccess(role, type, category, 'customers') && (
                    <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'contracts') && (
                    <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'promoters') && (
                    <NavLinkItem to="/promoters" icon={Users}>Promotores</NavLinkItem>
                  )}
                  {canAccess(role, type, category, 'users') && (
                    <NavLinkItem to="/users" icon={UserCog}>Trabajadores</NavLinkItem>
                  )}
                  {type === 'MASTER' && (
                    <NavLinkItem to="/sync" icon={RefreshCw}>Base de Datos Sync</NavLinkItem>
                  )}
                </>
              )}
            </div>
          </MobileMenuModal>
        );
      })}

      {/* More Menu */}
      {hasMore && (
        <MobileMenuModal open={activeTab === "more"} onClose={() => setActiveTab(null)} title="Más" icon={LayoutGrid}>
          {moreTabs.map((tab) => {
            const config = menuSections[tab];
            return (
              <div key={tab} className="mobile-menu-section">
                {tab === 'puntos' && hasPuntosVenta && (
                  <>
                    <div className="mobile-menu-section-title">Canales</div>
                    {canAccess(role, type, category, 'machines') && (
                      <NavLinkItem to="/machines" icon={Monitor}>ONEs</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'minis') && (
                      <NavLinkItem to="/minis" icon={Boxes}>MINIs</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'operativa' && hasOperativa && (
                  <>
                    <div className="mobile-menu-section-title">Operativa</div>
                    {canAccess(role, type, category, 'customers') && (
                      <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'contracts') && (
                      <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'incidences') && (
                      <NavLinkItem to="/incidences" icon={Info}>Incidencias</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'audit') && (
                      <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'tpvs') && (
                      <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'ventas' && hasVentas && (
                  <>
                    <div className="mobile-menu-section-title">Reporte</div>
                    {canAccess(role, type, category, 'sales') && (
                      <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'arqueos') && (
                      <>
                        <NavLinkItem to="/arqueos" icon={BarChart2}>Gráficos</NavLinkItem>
                        <NavLinkItem to="/arqueossales" icon={PieChart}>KPI</NavLinkItem>
                      </>
                    )}
                    {canAccess(role, type, category, 'objectives') && (
                      <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'comissions') && (
                      <NavLinkItem to="/comissions" icon={Calculator}>Comisiones</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'fichajes' && hasFichajes && (
                  <>
                    <div className="mobile-menu-section-title">Fichajes</div>
                    {canAccess(role, type, category, 'shifts') && (
                      <NavLinkItem to="/shifts" icon={Clock}>Jornada Promotor</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'soporte' && hasSoporte && (
                  <>
                    <div className="mobile-menu-section-title">Soporte</div>
                    {canAccess(role, type, category, 'supportticketadd') && (
                      <NavLinkItem to="/supportticketadd" icon={Info}>Crear Ticket</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'supporttickets') && (
                      <NavLinkItem to="/supporttickets" icon={Info}>Tickets</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'supportchat') && (
                      <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'faqs') && (
                      <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'manuals') && (
                      <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'api-docs') && (
                      <NavLinkItem to="/api-docs" icon={Newspaper}>API</NavLinkItem>
                    )}
                  </>
                )}

                {tab === 'admin' && hasAdmin && (
                  <>
                    <div className="mobile-menu-section-title">Administración</div>
                    {canAccess(role, type, category, 'promoters') && (
                      <NavLinkItem to="/promoters" icon={Users}>Promotores</NavLinkItem>
                    )}
                    {canAccess(role, type, category, 'users') && (
                      <NavLinkItem to="/users" icon={UserCog}>Trabajadores</NavLinkItem>
                    )}
                    {type === 'MASTER' && (
                      <NavLinkItem to="/sync" icon={RefreshCw}>Base de Datos Sync</NavLinkItem>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </MobileMenuModal>
      )}

      {/* Profile Menu */}
      <MobileMenuModal
        open={activeTab === "profile"}
        onClose={() => setActiveTab(null)}
        title="Perfil"
        isProfile={true}
        fullname={fullname}
        role={role}
      >
        <div className="mobile-menu-section">
          
          <div className="mobile-menu-divider" />

          <div className="mobile-menu-section-title">Configuración</div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <SlidersHorizontal size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Tema</span>
            </div>
            <DarkModeToggle />
          </div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <LayoutGrid size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Idioma</span>
            </div>
            <div style={{ position: 'relative', zIndex: 20 }}>
              <GoogleTranslate />
            </div>
          </div>

          <div className="mobile-menu-divider" />

          <button className="mobile-menu-item" onClick={onLogout}>
            <LogOut size={20} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </MobileMenuModal>
    </>
  );
};

/**
 * Main AppShell Component
 */
const AppShellBase = ({
  children,
  fullname,
  userId,
  role,
  type,
  category,
  authLogout,
  push,
}) => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState(null);
  const [showTools, setShowTools] = useState(false);

  useEffect(() => { injectStyles(); }, []);

  // Command Palette keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Viewport watcher
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, []);

  const onNavigate = () => {
    if (isMobile) {
      setMobileOpen(false);
      setMobileActiveTab(null);
    }
  };

  const onLogout = () => {
    authLogout();
    push('/login');
  };

  const goToUser = (userId) => {
    // if (role === 'ADMIN') {
    //   push(`/user/${userId}`);
    // }
  };

  const handleToggleSidebarMobile = () => {
    setMobileOpen(prev => !prev);
  };

  const toggleTools = () => setShowTools(prev => !prev);

  const topFunction = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowTools(false);
  };

  const bottomFunction = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const refreshFunction = () => {
    window.location.reload();
  };

  return (
    <ToastProvider>
      <div className="shell">
        <TopBar
          fullname={fullname}
          userId={userId}
          onLogout={onLogout}
          isMobile={isMobile}
          role={role}
          type={type}
          category={category}
          onToggleSidebarMobile={handleToggleSidebarMobile}
          onOpenCommandPalette={() => setCommandPaletteOpen(true)}
          onGoToUser={goToUser}
        />

        <CommandPalette
          isOpen={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          role={role}
          type={type}
          category={category}
          canAccess={canAccess}
          hasAccessToAny={hasAccessToAny}
          push={push}
        />

        {!isMobile && (
          <div className="sidebar-wrapper">
            <Sidebar
              role={role}
              type={type}
              category={category}
              onNavigate={onNavigate}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
            />
          </div>
        )}

        <main className={`content ${!isMobile ? (activeMenu ? 'shifted' : 'not-shifted') : ''}`}>
          {/* Breadcrumbs */}
          <Breadcrumbs category={category} />

          {children}
        </main>

        {isMobile && (
          <MobileBottomNav
            role={role}
            type={type}
            category={category}
            onNavigate={onNavigate}
            activeTab={mobileActiveTab}
            setActiveTab={setMobileActiveTab}
            fullname={fullname}
            userId={userId}
            onLogout={onLogout}
            onGoToUser={goToUser}
          />
        )}

        <div className={showTools ? "floating-tools-container-open" : "floating-tools-container"}>
          <button
            className={showTools ? "floating-button-open" : "floating-button"}
            onClick={toggleTools}
          >
            <i className="fas fa-cog"></i>
          </button>

          <div
            className={`tool-button top-tool ${showTools ? "visible" : ""}`}
            onClick={topFunction}
          >
            <i className="fas fa-arrow-up"></i>
          </div>

          <div
            className={`tool-button left-tool ${showTools ? "visible" : ""}`}
            onClick={refreshFunction}
          >
            <i className="fas fa-refresh"></i>
          </div>

          <div
            className={`tool-button bottom-tool ${showTools ? "visible" : ""}`}
            onClick={bottomFunction}
          >
            <i className="fas fa-arrow-down"></i>
          </div>
        </div>
      </div>
    </ToastProvider>
  );
};

const mapStateToProps = (state) => ({
  fullname: state.auth.fullname,
  userId: state.auth.id,
  role: state.auth.role,
  type: state.auth.type,
  category: state.auth.category,
});

const mapDispatchToProps = (dispatch) => ({
  authLogout: bindActionCreators(authLogout, dispatch),
  push: bindActionCreators(push, dispatch),
});

export const AppShell = connect(mapStateToProps, mapDispatchToProps)(AppShellBase);
